import React, { useState, useEffect } from 'react';
import "../../css/Notices.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FaBullhorn, FaSearch, FaTimes, FaFilter, FaEye, FaDownload, FaPaperclip,
    FaCalendarAlt, FaUserTag, FaFileAlt
} from 'react-icons/fa';
import Loader from "../../components/Loader";

function Notices() {
    const [notices, setNotices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedNotice, setSelectedNotice] = useState(null);

    const categories = [
        { id: 'general', name: 'General' },
        { id: 'academic', name: 'Academic' },
        { id: 'examination', name: 'Examination' },
        { id: 'event', name: 'Event' },
        { id: 'holiday', name: 'Holiday' },
        { id: 'emergency', name: 'Emergency' },
    ];

    const audienceOptions = [
        { id: 'all', name: 'Everyone' },
        { id: 'students', name: 'Students' },
        { id: 'teachers', name: 'Teachers' },
        { id: 'guardians', name: 'Guardians' },
        { id: 'staff', name: 'Administrative Staff' },
    ];

    // Fetch all notices
    const fetchNotices = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("token");

        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            setIsLoading(false);
            return;
        }
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/notices", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch notices");
            }
            const data = await response.json();
            setNotices(data);
        } catch (error) {
            console.error("Error fetching notices:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchNotices();
    }, []);

    // Filter and sort functions
    const getFilteredNotices = () => {
        if (!notices.length) return [];

        return notices.filter(notice => {
            const matchesSearch =
                notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notice.content.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesCategory =
                filterCategory === 'all' || notice.category === filterCategory;

            return matchesSearch && matchesCategory;
        });
    };

    const getSortedNotices = () => {
        const filteredData = getFilteredNotices();
        if (!sortConfig.key) return filteredData;

        return [...filteredData].sort((a, b) => {
            if (sortConfig.key === 'createdAt' || sortConfig.key === 'startDate') {
                const dateA = new Date(a[sortConfig.key]);
                const dateB = new Date(b[sortConfig.key]);
                return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
            }
            return sortConfig.direction === 'asc'
                ? a[sortConfig.key].localeCompare(b[sortConfig.key])
                : b[sortConfig.key].localeCompare(a[sortConfig.key]);
        });
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortDirectionIndicator = (column) => {
        if (sortConfig.key !== column) return null;
        return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
    };

    // Helper functions
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const getCategoryLabel = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    const getAudienceLabels = (audienceArr) => {
        if (audienceArr.includes('all')) return 'Everyone';
        return audienceArr.map(aud => {
            const option = audienceOptions.find(opt => opt.id === aud);
            return option ? option.name : aud;
        }).join(', ');
    };

    const getPriorityBadgeClass = (priority) => {
        switch (priority) {
            case 'low': return 'notices-priority-low';
            case 'normal': return 'notices-priority-normal';
            case 'high': return 'notices-priority-high';
            case 'urgent': return 'notices-priority-urgent';
            default: return 'notices-priority-normal';
        }
    };

    const isNoticeActive = (notice) => {
        const now = new Date();
        const startDate = new Date(notice.startDate);
        const endDate = notice.endDate ? new Date(notice.endDate) : null;
        return startDate <= now && (!endDate || endDate >= now);
    };

    const getStatusBadgeClass = (notice) => {
        if (isNoticeActive(notice)) return 'notices-status-active';
        const now = new Date();
        return new Date(notice.startDate) > now ? 'notices-status-scheduled' : 'notices-status-expired';
    };

    const getStatusText = (notice) => {
        if (isNoticeActive(notice)) return 'Active';
        const now = new Date();
        return new Date(notice.startDate) > now ? 'Scheduled' : 'Expired';
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterCategory('all');
        setSortConfig({ key: 'createdAt', direction: 'desc' });
    };

    // Modal handler
    const openViewModal = (notice) => {
        setSelectedNotice(notice);
        setShowViewModal(true);
    };

    return (
        <div className="notices-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            <div className="notices-header">
                <div className="notices-title-container">
                    <FaBullhorn className="notices-title-icon" />
                    <h1 className="notices-title">All Notices</h1>
                </div>

                <div className="notices-stats">
                    <div className="notices-stat-box">
                        <div className="notices-stat-icon notices-stat-icon-total">
                            <FaBullhorn />
                        </div>
                        <div className="notices-stat-info">
                            <span className="notices-stat-number">{notices.length}</span>
                            <span className="notices-stat-label">Total Notices</span>
                        </div>
                    </div>
                    <div className="notices-stat-box">
                        <div className="notices-stat-icon notices-stat-icon-active">
                            <FaBullhorn />
                        </div>
                        <div className="notices-stat-info">
                            <span className="notices-stat-number">
                                {notices.filter(isNoticeActive).length}
                            </span>
                            <span className="notices-stat-label">Active Notices</span>
                        </div>
                    </div>
                    <div className="notices-stat-box">
                        <div className="notices-stat-icon notices-stat-icon-scheduled">
                            <FaCalendarAlt />
                        </div>
                        <div className="notices-stat-info">
                            <span className="notices-stat-number">
                                {notices.filter(n => new Date(n.startDate) > new Date()).length}
                            </span>
                            <span className="notices-stat-label">Scheduled Notices</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="notices-filters">
                <div className="notices-filter-row">
                    <div className="notices-filter-group">
                        <label className="notices-filter-label">Filter by Category</label>
                        <select
                            className="notices-filter-select"
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="notices-filter-group">
                        <label className="notices-filter-label">Sort By</label>
                        <select
                            className="notices-filter-select"
                            value={`${sortConfig.key}-${sortConfig.direction}`}
                            onChange={(e) => {
                                const [key, direction] = e.target.value.split('-');
                                setSortConfig({ key, direction });
                            }}
                        >
                            <option value="createdAt-desc">Date Created (Newest First)</option>
                            <option value="createdAt-asc">Date Created (Oldest First)</option>
                            <option value="startDate-desc">Start Date (Newest First)</option>
                            <option value="startDate-asc">Start Date (Oldest First)</option>
                            <option value="title-asc">Title (A-Z)</option>
                            <option value="title-desc">Title (Z-A)</option>
                        </select>
                    </div>
                </div>

                <div className="notices-search-container">
                    <FaSearch className="notices-search-icon" />
                    <input
                        type="text"
                        className="notices-search-input"
                        placeholder="Search notices by title or content..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="notices-search-clear" onClick={() => setSearchTerm('')}>
                            <FaTimes />
                        </button>
                    )}
                    <button className="notices-btn notices-btn-clear-filters" onClick={clearFilters}>
                        <FaTimes /> Clear Filters
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="notices-loading">
                    <Loader />
                    <p>Loading notices...</p>
                </div>
            ) : (
                <div className="notices-content">
                    {notices.length === 0 ? (
                        <div className="notices-empty">
                            <FaBullhorn className="notices-empty-icon" />
                            <h2>No Notices Available</h2>
                            <p>There are currently no notices to display.</p>
                        </div>
                    ) : getSortedNotices().length === 0 ? (
                        <div className="notices-no-results">
                            <h3>No Matching Notices</h3>
                            <p>No notices match your current search or filter criteria.</p>
                            <button className="notices-btn notices-btn-secondary" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        </div>
                    ) : (
                        <div className="notices-grid">
                            {getSortedNotices().map((notice) => (
                                <div key={notice._id} className={`notices-card notices-card-${getStatusText(notice).toLowerCase()}`}>
                                    <div className="notices-card-header">
                                        <h3 className="notices-card-title">
                                            {notice.title}
                                            {notice.attachmentUrl && (
                                                <span className="notices-attachment-indicator">
                                                    <FaPaperclip />
                                                </span>
                                            )}
                                        </h3>
                                        <span className={`notices-status-badge ${getStatusBadgeClass(notice)}`}>
                                            {getStatusText(notice)}
                                        </span>
                                    </div>

                                    <div className="notices-card-body">
                                        <div className="notices-card-meta">
                                            <span className={`notices-category-badge notices-category-${notice.category}`}>
                                                {getCategoryLabel(notice.category)}
                                            </span>
                                            <span className={`notices-priority-badge ${getPriorityBadgeClass(notice.priority)}`}>
                                                {notice.priority.charAt(0).toUpperCase() + notice.priority.slice(1)}
                                            </span>
                                        </div>

                                        <p className="notices-card-content">
                                            {notice.content.length > 150
                                                ? `${notice.content.substring(0, 150)}...`
                                                : notice.content}
                                        </p>

                                        <div className="notices-card-details">
                                            <div className="notices-card-detail">
                                                <FaCalendarAlt /> <span>{formatDate(notice.startDate)}</span>
                                                {notice.endDate && <span> - {formatDate(notice.endDate)}</span>}
                                            </div>
                                            <div className="notices-card-detail">
                                                <FaUserTag /> <span>{getAudienceLabels(notice.targetAudience)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="notices-card-footer">
                                        {notice.attachmentUrl && (
                                            <a
                                                href={notice.attachmentUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="notices-attachment-link"
                                            >
                                                <FaDownload /> Download Attachment
                                            </a>
                                        )}
                                        <button 
                                            className="notices-btn notices-btn-view"
                                            onClick={() => openViewModal(notice)}
                                        >
                                            <FaEye /> View Details
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* View Notice Modal */}
            {showViewModal && selectedNotice && (
                <div className="notices-modal-overlay">
                    <div className="notices-modal notices-view-modal">
                        <div className="notices-modal-header">
                            <h2 className="notices-modal-title">Notice Details</h2>
                            <button
                                className="notices-modal-close"
                                onClick={() => setShowViewModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="notices-modal-content">
                            <div className="notices-view-content">
                                <div className="notices-view-header">
                                    <h3 className="notices-view-title">{selectedNotice.title}</h3>
                                    <div className="notices-view-meta">
                                        <span className={`notices-priority-badge ${getPriorityBadgeClass(selectedNotice.priority)}`}>
                                            {selectedNotice.priority.charAt(0).toUpperCase() + selectedNotice.priority.slice(1)} Priority
                                        </span>
                                        <span className={`notices-status-badge ${getStatusBadgeClass(selectedNotice)}`}>
                                            {getStatusText(selectedNotice)}
                                        </span>
                                        <span className={`notices-category-badge notices-category-${selectedNotice.category}`}>
                                            {getCategoryLabel(selectedNotice.category)}
                                        </span>
                                    </div>
                                </div>

                                <div className="notices-view-details">
                                    <div className="notices-view-detail">
                                        <span className="notices-view-label"><FaCalendarAlt /> Posted:</span>
                                        <span className="notices-view-value">{formatDate(selectedNotice.createdAt)}</span>
                                    </div>
                                    <div className="notices-view-detail">
                                        <span className="notices-view-label"><FaCalendarAlt /> Active From:</span>
                                        <span className="notices-view-value">{formatDate(selectedNotice.startDate)}</span>
                                    </div>
                                    {selectedNotice.endDate && (
                                        <div className="notices-view-detail">
                                            <span className="notices-view-label"><FaCalendarAlt /> Until:</span>
                                            <span className="notices-view-value">{formatDate(selectedNotice.endDate)}</span>
                                        </div>
                                    )}
                                    <div className="notices-view-detail">
                                        <span className="notices-view-label"><FaUserTag /> Audience:</span>
                                        <span className="notices-view-value">{getAudienceLabels(selectedNotice.targetAudience)}</span>
                                    </div>
                                </div>

                                <div className="notices-view-body">
                                    <h4 className="notices-view-section-title">Notice Content</h4>
                                    <div className="notices-view-text">
                                        {selectedNotice.content}
                                    </div>
                                </div>

                                {selectedNotice.attachmentUrl && (
                                    <div className="notices-view-attachment">
                                        <h4 className="notices-view-section-title">Attachment</h4>
                                        <a
                                            href={selectedNotice.attachmentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="notices-attachment-link"
                                        >
                                            <FaFileAlt className="notices-attachment-icon" />
                                            <span>{selectedNotice.attachmentUrl.split('/').pop()}</span>
                                            <FaDownload className="notices-download-icon" />
                                        </a>
                                    </div>
                                )}

                                <div className="notices-view-actions">
                                    <button
                                        className="notices-btn notices-btn-primary"
                                        onClick={() => setShowViewModal(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Notices;